import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { fetchWithAuth } from '../util/fetchWithAuth';
import { PaymentRequestDetail } from '../types';

const PaymentRequestDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [paymentRequest, setPaymentRequest] = useState<PaymentRequestDetail | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [showPayload, setShowPayload] = useState(false);

  useEffect(() => {
    const fetchPaymentRequest = async () => {
      setIsLoading(true);
      try {
        const res = await fetchWithAuth(`/delta_pay_payment_requests/${id}`, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('jwt')}`
          }
        });

        if (!res.ok) {
          throw new Error('Failed to fetch payment request');
        }

        const data = await res.json();
        setPaymentRequest(data);
      } catch (error) {
        console.error('Error fetching payment request:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchPaymentRequest();
  }, [id]);

  const formatDateTime = (dateString: string | null | undefined) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return isNaN(date.getTime()) ? 'N/A' : date.toLocaleString();
  };

  const formatAmount = (amount: string) => {
    const num = parseFloat(amount);
    return isNaN(num) ? amount : `E${num.toFixed(2)}`;
  };

  const getTransactionStatusBadge = (status: string | null) => {
    const baseClasses = 'px-3 py-1 text-sm font-medium rounded-full';

    switch (status) {
      case 'succeeded':
        return <span className={`${baseClasses} bg-green-100 text-green-800`}>Succeeded</span>;
      case 'failed':
        return <span className={`${baseClasses} bg-red-100 text-red-800`}>Failed</span>;
      default:
        return <span className={`${baseClasses} bg-gray-100 text-gray-800`}>Pending</span>;
    }
  };

  const getRequestStatusBadge = (status: string | null) => {
    const baseClasses = 'px-3 py-1 text-sm font-medium rounded-full';

    if (!status || status === 'pending') {
      return <span className={`${baseClasses} bg-yellow-100 text-yellow-800`}>Pending</span>;
    }
    if (status === 'completed') {
      return <span className={`${baseClasses} bg-green-100 text-green-800`}>Completed</span>;
    }
    return <span className={`${baseClasses} bg-gray-100 text-gray-800`}>{status}</span>;
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-gray-600">Loading payment request...</div>
      </div>
    );
  }

  if (!paymentRequest) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-gray-600">Payment request not found</div>
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <div className="mb-6">
        <button
          onClick={() => navigate('/payment-requests')}
          className="text-blue-600 hover:text-blue-800 mb-4 flex items-center"
        >
          ← Back to Payment Requests
        </button>
        <h1 className="text-2xl font-bold text-gray-900 mb-2">Payment Request Details</h1>
        <p className="text-gray-600 font-mono text-sm">{paymentRequest.delta_pay_metadata_uuid}</p>
      </div>

      <div className="space-y-6">
        {/* Payment Information */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Payment Information</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-500">Amount</label>
              <p className="mt-1 text-gray-900">{formatAmount(paymentRequest.delta_pay_amount)}</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-500">Username</label>
              <p className="mt-1 text-gray-900">{paymentRequest.delta_pay_username}</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-500">Account ID</label>
              <p className="mt-1 text-gray-900">{paymentRequest.delta_pay_account_id || 'N/A'}</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-500">Transaction ID</label>
              <p className="mt-1 text-gray-900 font-mono text-sm">{paymentRequest.delta_pay_transaction_id || 'N/A'}</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-500">Transaction Type</label>
              <p className="mt-1 text-gray-900">{paymentRequest.delta_pay_transaction_type}</p>
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-500">Notes</label>
              <p className="mt-1 text-gray-900">{paymentRequest.delta_pay_notes || 'N/A'}</p>
            </div>
          </div>
        </div>

        {/* Status Information */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Status Information</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-500">Request Status</label>
              <div className="mt-1">{getRequestStatusBadge(paymentRequest.delta_pay_payment_request_status)}</div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-500">Transaction Status</label>
              <div className="mt-1">{getTransactionStatusBadge(paymentRequest.delta_pay_transaction_status)}</div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-500">Initialized At</label>
              <p className="mt-1 text-gray-900">{formatDateTime(paymentRequest.delta_pay_initialized_at)}</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-500">Settled At</label>
              <p className="mt-1 text-gray-900">{formatDateTime(paymentRequest.delta_pay_settled_at)}</p>
            </div>
            {paymentRequest.error_message && (
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-red-600">Error Message</label>
                <div className="mt-1 bg-red-50 border border-red-200 rounded-md p-3">
                  <p className="text-red-800 text-sm">{paymentRequest.error_message}</p>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Order Information */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Order Information</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-500">Order Reference</label>
              <button
                onClick={() => navigate(`/orders/${paymentRequest.order.id}`)}
                className="mt-1 text-blue-600 hover:text-blue-800 hover:underline"
              >
                {paymentRequest.order.reference}
              </button>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-500">Order Status</label>
              <p className="mt-1 text-gray-900 capitalize">{paymentRequest.order.status.replace(/_/g, ' ')}</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-500">Total Price</label>
              <p className="mt-1 text-gray-900">{formatAmount(paymentRequest.order.total_price)}</p>
            </div>
          </div>
        </div>

        {/* Customer Information */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Customer Information</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-500">Customer Name</label>
              <button
                onClick={() => navigate(`/customers/${paymentRequest.customer.id}/edit`)}
                className="mt-1 text-blue-600 hover:text-blue-800 hover:underline"
              >
                {paymentRequest.customer.full_name}
              </button>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-500">Customer Code</label>
              <p className="mt-1 text-gray-900">{paymentRequest.customer.customer_code}</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-500">Phone Number</label>
              <p className="mt-1 text-gray-900">{paymentRequest.customer.phone_number}</p>
            </div>
          </div>
        </div>

        {/* API Payload */}
        {paymentRequest.delta_pay_payload && (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <button
              onClick={() => setShowPayload(!showPayload)}
              className="flex items-center justify-between w-full text-left"
            >
              <h2 className="text-lg font-semibold text-gray-900">API Payload</h2>
              <span className="text-gray-500">{showPayload ? '▼' : '▶'}</span>
            </button>
            {showPayload && (
              <div className="mt-4">
                <pre className="bg-gray-50 border border-gray-200 rounded-md p-4 overflow-x-auto text-xs">
                  {JSON.stringify(paymentRequest.delta_pay_payload, null, 2)}
                </pre>
              </div>
            )}
          </div>
        )}

        {/* Timestamps */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Timestamps</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-500">Created At</label>
              <p className="mt-1 text-gray-900">{formatDateTime(paymentRequest.created_at)}</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-500">Updated At</label>
              <p className="mt-1 text-gray-900">{formatDateTime(paymentRequest.updated_at)}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PaymentRequestDetailPage;
